/**
 * The supported locales, and the direction each one lays out in.
 *
 * en + ar from day one is a decision, not an aspiration: retrofitting RTL after
 * a UI exists means auditing every margin, every icon and every alignment in the
 * product. See docs/architecture/0009-i18n-and-rtl.md.
 */

export const LOCALES = ['en', 'ar'] as const

export type Locale = (typeof LOCALES)[number]

export const DEFAULT_LOCALE: Locale = 'en'

export type Direction = 'ltr' | 'rtl'

const DIRECTIONS: Record<Locale, Direction> = {
  en: 'ltr',
  ar: 'rtl',
}

export const LOCALE_LABELS: Record<Locale, string> = {
  en: 'English',
  ar: 'العربية',
}

export function isLocale(value: string): value is Locale {
  return (LOCALES as readonly string[]).includes(value)
}

export function directionFor(locale: string): Direction {
  return isLocale(locale) ? DIRECTIONS[locale] : 'ltr'
}

/**
 * The BCP-47 tag handed to `Intl`, which is not always the i18next locale.
 *
 * `ar` alone resolves to Eastern Arabic-Indic digits (٠١٢٣) in most runtimes.
 * We force Western digits with the `-u-nu-latn` extension — see the note in
 * formatters.ts for why.
 */
export function intlLocale(locale: string): string {
  return locale === 'ar' ? 'ar-u-nu-latn' : locale
}
